import * as React from 'react';

import {
  MosaicCollapseOptionsById,
  MosaicCollapsedById,
} from 'src/components/mosaic/types';
import {
  EMPTY_TILE_ID,
  MosaicSplitOptions,
  MosaicSplitType,
  NestedMosaicParentInitial,
  ProjectSidebarTileId,
} from 'src/constants/mosaic';
import { useDidValueChange } from 'src/utils/hooks/useDidValueChange';

export const useMosaicProps = (
  filter: string,
  hasMappingsItems: boolean | null,
  hasQuestionsItems: boolean | null,
  hasResourcesItems: boolean
) => {
  const hasMappings = hasMappingsItems !== null;
  const didHasMappingsChange = useDidValueChange(hasMappings);

  const hasQuestions = hasQuestionsItems !== null;
  const didHasQuestionsChange = useDidValueChange(hasQuestions);

  const [collapsedById, setCollapsedById] = React.useState<
    MosaicCollapsedById<ProjectSidebarTileId>
  >({
    [ProjectSidebarTileId.MAPPINGS]: hasMappings ? false : !hasMappingsItems,
    [ProjectSidebarTileId.COMPETENCY_QUESTIONS]: hasMappings
      ? true
      : !hasQuestionsItems,
    [ProjectSidebarTileId.RESOURCES]: hasMappings ? true : !hasResourcesItems,
  });
  const prevCollapsedById =
    React.useRef<MosaicCollapsedById<ProjectSidebarTileId>>(collapsedById);

  const didFilterChange = useDidValueChange(filter);
  React.useEffect(() => {
    if (didFilterChange) {
      setCollapsedById(
        filter
          ? {
              [ProjectSidebarTileId.MAPPINGS]: !hasMappingsItems,
              [ProjectSidebarTileId.COMPETENCY_QUESTIONS]: !hasQuestionsItems,
              [ProjectSidebarTileId.RESOURCES]: !hasResourcesItems,
            }
          : prevCollapsedById.current
      );
    }
  }, [
    didFilterChange,
    filter,
    hasMappingsItems,
    hasQuestionsItems,
    hasResourcesItems,
  ]);

  React.useEffect(() => {
    if (!filter) {
      prevCollapsedById.current = collapsedById;
    }
  }, [collapsedById, filter]);

  React.useEffect(() => {
    if (didHasMappingsChange || didHasQuestionsChange) {
      setCollapsedById({
        [ProjectSidebarTileId.MAPPINGS]: hasMappings
          ? false
          : !hasMappingsItems,
        [ProjectSidebarTileId.COMPETENCY_QUESTIONS]: hasMappings
          ? true
          : !hasQuestionsItems,
        [ProjectSidebarTileId.RESOURCES]: hasMappings
          ? true
          : !hasResourcesItems,
      });
    }
  }, [
    didHasMappingsChange,
    didHasQuestionsChange,
    hasMappings,
    hasMappingsItems,
    hasQuestionsItems,
    hasResourcesItems,
  ]);

  const collapseOptions: MosaicCollapseOptionsById<ProjectSidebarTileId> =
    React.useMemo(
      () => ({
        [ProjectSidebarTileId.MAPPINGS]: {
          collapsible: true,
          collapsed: Boolean(collapsedById[ProjectSidebarTileId.MAPPINGS]),
          collapsedSize: 60,
          collapsedVisible: true,
        },
        [ProjectSidebarTileId.COMPETENCY_QUESTIONS]: {
          collapsible: true,
          collapsed: Boolean(
            collapsedById[ProjectSidebarTileId.COMPETENCY_QUESTIONS]
          ),
          collapsedSize: 60,
          collapsedVisible: true,
        },
        [ProjectSidebarTileId.RESOURCES]: {
          collapsible: true,
          collapsed: Boolean(collapsedById[ProjectSidebarTileId.RESOURCES]),
          collapsedSize: 60,
          collapsedVisible: true,
        },
      }),
      [collapsedById]
    );

  const { initialValue, splitOptions } = React.useMemo(() => {
    let questionsResourcesValue:
      | ProjectSidebarTileId
      | NestedMosaicParentInitial<ProjectSidebarTileId>;
    let questionsResourcesOptions: null | MosaicSplitOptions;
    if (hasQuestionsItems === null) {
      questionsResourcesValue = ProjectSidebarTileId.RESOURCES;
      questionsResourcesOptions = null;
    } else {
      questionsResourcesValue = {
        first: ProjectSidebarTileId.COMPETENCY_QUESTIONS,
        second: ProjectSidebarTileId.RESOURCES,
      };
      questionsResourcesOptions = {
        firstMinSplitType: MosaicSplitType.NUMBER,
        firstMinSplitValue: 120,
        secondMinSplitType: MosaicSplitType.NUMBER,
        secondMinSplitValue: 120,
        initialSplitPercentage: 50,
      };
    }

    let mappingsQuestionsValue:
      | ProjectSidebarTileId
      | NestedMosaicParentInitial<ProjectSidebarTileId>;
    let mappingsQuestionsOptions: null | MosaicSplitOptions;
    if (hasMappingsItems === null) {
      mappingsQuestionsValue = questionsResourcesValue;
      mappingsQuestionsOptions = questionsResourcesOptions;
    } else {
      mappingsQuestionsValue = {
        first: ProjectSidebarTileId.MAPPINGS,
        second: questionsResourcesValue,
        splitPercentage: 100,
      };
      mappingsQuestionsOptions = {
        first: questionsResourcesOptions || undefined,
        firstMinSplitType: MosaicSplitType.NUMBER,
        firstMinSplitValue: 120,
        secondMinSplitType: MosaicSplitType.NUMBER,
        secondMinSplitValue: questionsResourcesOptions ? 250 : 120,
        initialSplitPercentage: 50,
      };
    }

    const initialValue: NestedMosaicParentInitial<
      ProjectSidebarTileId | typeof EMPTY_TILE_ID
    > =
      typeof mappingsQuestionsValue === 'string'
        ? {
            first: mappingsQuestionsValue,
            second: EMPTY_TILE_ID,
          }
        : mappingsQuestionsValue;

    const splitOptions: MosaicSplitOptions = mappingsQuestionsOptions || {
      firstMinSplitType: MosaicSplitType.NUMBER,
      firstMinSplitValue: 120,
      secondMinSplitType: MosaicSplitType.NUMBER,
      secondMinSplitValue: 120,
      initialSplitPercentage: 50,
    };

    return { initialValue, splitOptions };
  }, [hasMappingsItems, hasQuestionsItems]);

  return { collapseOptions, initialValue, splitOptions, setCollapsedById };
};
